import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import './ThinkingProcessAccordion.css';

const formatSeconds = (seconds) => {
  return `${seconds}s`;
};

const ThinkingProcessLog = ({ isVisible, steps = [], status = 'complete' }) => {
  // Debug: log every render
  console.log('ThinkingProcessLog render', { isVisible, stepsLength: steps.length, status, steps });

  const [isExpanded, setIsExpanded] = useState(status === 'in_progress');
  const [timer, setTimer] = useState(0);
  const intervalRef = useRef(null);

  // Calculate total duration from completed steps
  const totalDurationMs = steps.reduce((total, step) => {
    if (step.duration_ms) {
      return total + step.duration_ms;
    }
    return total;
  }, 0);
  
  const totalDurationSeconds = Math.round(totalDurationMs / 1000);

  // Timer logic - only use timer for in-progress status
  useEffect(() => {
    if (status === 'in_progress') {
      intervalRef.current = setInterval(() => {
        setTimer((prev) => prev + 1);
      }, 1000);
    } else if (status === 'complete') {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [status]);

  // Reset timer when log is shown for a new process
  useEffect(() => {
    if (status === 'in_progress') setTimer(0);
  }, [status]);

  if (!isVisible) return null;
  
  const shouldShowHeader = status === 'in_progress' || steps.length > 0;
  const isInProgress = status === 'in_progress';
  const displayDuration = status === 'complete' && totalDurationSeconds > 0 ? totalDurationSeconds : timer;

  return (
    <div className={`thinking-process-log ${isInProgress ? 'in-progress' : ''}`}>
      {shouldShowHeader && (
        <div className={`log-header ${isInProgress ? 'in-progress' : ''}`} onClick={() => setIsExpanded(!isExpanded)}>
          <span className="log-toggle">{isExpanded ? '\u25bc' : '\u25b6'}</span>
          <span>AI Thinking Log</span>
          <span className={`log-timer ${isInProgress ? 'in-progress' : ''}`}>{formatSeconds(displayDuration)}</span>
          {isInProgress && <span className="log-status working"> (working...)</span>}
        </div>
      )}
      {isExpanded && (
        <div className="log-content-centered">
          {steps.length === 0 && isInProgress ? (
            <div className="log-step-thinking">Starting analysis...</div>
          ) : (
            steps.map((step) => (
              <div key={step.step_id} className="log-step-block">
                <div className="log-step-title">
                  [{step.title || step.step_name}]
                  {step.duration_ms && (
                    <span className="step-duration"> ({Math.round(step.duration_ms / 1000)}s)</span>
                  )}
                </div>
                {/* Summary/Markdown output */}
                {Array.isArray(step.transcript) && step.transcript.length > 0 ? (
                  step.transcript.map((line, i) => (
                    <div className="log-step-markdown" key={i}>
                      <ReactMarkdown>{line}</ReactMarkdown>
                    </div>
                  ))
                ) : step.status === 'in_progress' ? (
                  <div className="log-step-thinking">...thinking...</div>
                ) : step.status === 'failed' ? (
                  <div className="log-step-error">Error: {step.error_message || 'Unknown error'}</div>
                ) : step.text ? (
                  <div className="log-step-markdown">
                    <ReactMarkdown>{step.text}</ReactMarkdown>
                  </div>
                ) : null}
                {/* Raw output (collapsible) */}
                {step.raw_output && (
                  <details className="log-step-raw-output">
                    <summary>Raw Output</summary>
                    <pre>{JSON.stringify(step.raw_output, null, 2)}</pre>
                  </details>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ThinkingProcessLog; 